// v2 - axiosClient auth
import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from './lib/axiosClient';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'https://ivision-crm-backend-production.up.railway.app';
const STATUS_STYLES = {
  available: { bg: '#1a2a1a', color: '#4caf50', label: 'Available' },
  pending:   { bg: '#2a2a1a', color: '#f0c040', label: 'Pending' },
  sold:      { bg: '#1a1a2a', color: '#4ab0f0', label: 'Sold' },
  withdrawn: { bg: '#2a2a2a', color: '#555',    label: 'Withdrawn' },
};
const CONDITION_LABELS = { new:'New', like_new:'Like New', good:'Good', fair:'Fair', parts:'Parts Only' };

export default function SalesModule() {
  const [view, setView] = useState('list');
  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [consignors, setConsignors] = useState([]);
  const [clients, setClients] = useState([]);
  const [fleetItems, setFleetItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilter] = useState('');
  const [filterType, setType] = useState('');
  const [search, setSearch] = useState('');
  const [sellingItem, setSellingItem] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filterStatus) p.set('status', filterStatus);
    if (filterType) p.set('sale_type', filterType);
    if (search) p.set('search', search);
    try {
      const [iR, mR, cR] = await Promise.all([
        axiosClient.get('/api/rentals/sales/items?' + p),
        axiosClient.get('/api/rentals/sales/metrics'),
        axiosClient.get('/api/rentals/sales/consignors'),
      ]);
      const [iJ, mJ, cJ] = [iR.data, mR.data, cR.data];
      setItems(iJ.items || []);
      setMetrics(mJ.metrics || null);
      setConsignors(cJ.consignors || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterStatus, filterType, search]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    Promise.all([
      axiosClient.get('/api/rentals/clients').then(r => r.data),
      axiosClient.get('/api/rentals/items').then(r => r.data),
    ]).then(([cJ, iJ]) => {
      setClients(cJ.clients || cJ || []);
      setFleetItems(iJ.items || iJ || []);
    });
  }, []);

  const deleteItem = async (id) => {
    if (!window.confirm('Remove listing?')) return;
    await axiosClient.get('/api/rentals/sales/items/' + id);
    loadAll();
  };

  const updateStatus = async (id, status) => {
    await axiosClient.patch('/api/rentals/sales/items/' + id, { status });
    loadAll();
  };

  if (view === 'new') return <NewItemForm consignors={consignors} fleetItems={fleetItems} onSaved={() => { setView('list'); loadAll(); }} onCancel={() => setView('list')} />;
  if (view === 'consignors') return <ConsignorsPage consignors={consignors} onBack={() => { setView('list'); loadAll(); }} />;
  if (view === 'orders') return <SaleOrdersPage onBack={() => setView('list')} />;
  if (view === 'sell' && sellingItem) return <RecordSaleForm item={sellingItem} clients={clients} onSaved={() => { setView('list'); loadAll(); }} onCancel={() => { setView('list'); setSellingItem(null); }} />;

  return (
    <div style={s.page}>
      <div style={s.hdr}>
        <div><h2 style={s.title}>Sales</h2><p style={s.sub}>Equipment sales and consignment</p></div>
        <div style={{ display:'flex', gap:8 }}>
          <button style={s.btnSec} onClick={() => setView('orders')}>Sale History</button>
          <button style={s.btnSec} onClick={() => setView('consignors')}>Consignors</button>
          <button style={s.btn} onClick={() => setView('new')}>+ New Listing</button>
        </div>
      </div>
      {metrics && (
        <div style={s.mRow}>
          {[
            ['Available', metrics.available, '#4caf50'],
            ['Pending', metrics.pending, '#f0c040'],
            ['Sold', metrics.sold, '#4ab0f0'],
            ['Consignment', metrics.consignment_listed, '#c080f0'],
            ['Inventory $', '$'+(metrics.inventory_value||0).toLocaleString('en',{minimumFractionDigits:0}), '#888'],
            ['Revenue', '$'+(metrics.total_revenue||0).toLocaleString('en',{minimumFractionDigits:0}), '#4caf50'],
            ['Your Net', '$'+(metrics.your_net||0).toLocaleString('en',{minimumFractionDigits:0}), '#4caf50'],
            ['Consignor Owed', '$'+(metrics.consignor_owed||0).toLocaleString('en',{minimumFractionDigits:0}), '#f0c040'],
          ].map(([l,v,c]) => (
            <div key={l} style={s.mCard}><div style={{ fontSize:20, fontWeight:700, color:c }}>{v}</div><div style={s.mLbl}>{l}</div></div>
          ))}
        </div>
      )}
      <div style={s.fRow}>
        <input style={s.search} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={s.sel} value={filterStatus} onChange={e => setFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_STYLES).map(([v,c]) => <option key={v} value={v}>{c.label}</option>)}
        </select>
        <select style={s.sel} value={filterType} onChange={e => setType(e.target.value)}>
          <option value="">All Types</option>
          <option value="owned">Owned</option>
          <option value="consignment">Consignment</option>
        </select>
      </div>
      {loading ? <div style={s.empty}>Loading...</div> : items.length === 0 ? (
        <div style={s.empty}>No listings. <span style={{ color:'#4caf50', cursor:'pointer' }} onClick={() => setView('new')}>Create one</span></div>
      ) : (
        <div style={s.grid}>
          {items.map(item => {
            const sc = STATUS_STYLES[item.status] || STATUS_STYLES.available;
            return (
              <div key={item.id} style={s.card}>
                <div style={s.cardImg}>
                  {item.img_url ? <img src={item.img_url} alt={item.title} style={{ width:'100%',height:'100%',objectFit:'cover' }} /> : <span style={{ fontSize:40 }}>🏗</span>}
                  <span style={{ ...s.pill, top:8, right:8, background:sc.bg, color:sc.color }}>{sc.label}</span>
                  <span style={{ ...s.pill, top:8, left:8, background:item.sale_type==='consignment'?'#1a1a2a':'#1a2a1a', color:item.sale_type==='consignment'?'#c080f0':'#4caf50' }}>{item.sale_type==='consignment'?'Consignment':'Owned'}</span>
                </div>
                <div style={s.cardBody}>
                  <div style={{ fontWeight:700, fontSize:14, color:'#fff', marginBottom:4 }}>{item.title}</div>
                  <div style={{ fontSize:12, color:'#e2e8f0', marginBottom:4 }}>{[item.make,item.model,item.year].filter(Boolean).join(' ')}</div>
                  {item.serial_number && <div style={{ fontSize:11, color:'#e2e8f0' }}>S/N: {item.serial_number}</div>}
                  {item.hour_meter && <div style={{ fontSize:11, color:'#e2e8f0' }}>{item.hour_meter} hrs</div>}
                  {item.sale_type==='consignment' && item.rental_consignors && (
                    <div style={{ fontSize:11, color:'#c080f0', marginTop:6 }}>{item.rental_consignors.name} — {item.consignor_split_percent}% their cut</div>
                  )}
                  <div style={{ fontSize:20, fontWeight:700, color:'#4caf50', margin:'10px 0 4px' }}>${parseFloat(item.asking_price).toLocaleString()}</div>
                  {item.status==='sold' && item.sold_price && <div style={{ fontSize:12, color:'#e2e8f0' }}>Sold ${parseFloat(item.sold_price).toLocaleString()}</div>}
                  <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
                    {item.status!=='sold' && <button style={s.btnSell} onClick={() => { setSellingItem(item); setView('sell'); }}>Record Sale</button>}
                    {item.status==='available' && <button style={s.btnPend} onClick={() => updateStatus(item.id,'pending')}>Mark Pending</button>}
                    {item.status==='pending' && <button style={s.btnPend} onClick={() => updateStatus(item.id,'available')}>Back to Available</button>}
                    {item.status!=='sold' && <button style={{ background:'none',border:'none',color:'#444',cursor:'pointer',marginLeft:'auto' }} onClick={() => deleteItem(item.id)}>✕</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NewItemForm({ consignors, fleetItems, onSaved, onCancel }) {
  const [form, setForm] = useState({ sale_type:'owned', rental_item_id:'', consignor_id:'', consignor_split_percent:70, title:'', make:'', model:'', year:'', serial_number:'', hour_meter:'', condition:'good', asking_price:'', minimum_price:'', cost_basis:'', description:'' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k,v) => setForm(p => ({ ...p, [k]:v }));
  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const res = await axiosClient.post('/api/rentals/sales/items', { ...form, rental_item_id: form.rental_item_id||null, consignor_id: form.consignor_id||null });
      const json = res.data;
      if (res.status >= 400) throw new Error(json.error||'Failed');
      onSaved();
    } catch(e) { setError(e.message); } finally { setSaving(false); }
  };
  return (
    <div style={s.page}>
      <div style={s.hdr}>
        <h2 style={s.title}>New Listing</h2>
        <div style={{ display:'flex', gap:10 }}><button style={s.btnSec} onClick={onCancel}>Cancel</button><button style={s.btn} onClick={submit} disabled={saving}>{saving?'Saving...':'Create'}</button></div>
      </div>
      <div style={s.fBox}>
        <div style={{ display:'flex', gap:10, marginBottom:16 }}>
          {[['owned','Owned'],['consignment','Consignment']].map(([v,l]) => (
            <button key={v} style={{ ...s.tBtn, ...(form.sale_type===v?s.tBtnA:{}) }} onClick={() => set('sale_type',v)}>{l}</button>
          ))}
        </div>
        <div style={s.fGrid}>
          {form.sale_type==='consignment' ? <>
            <div style={s.ff}><label style={s.lbl}>Consignor *</label><select style={s.inp} value={form.consignor_id} onChange={e => set('consignor_id',e.target.value)}><option value="">Select...</option>{consignors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            <div style={s.ff}><label style={s.lbl}>Their Split %</label><input style={s.inp} type="number" value={form.consignor_split_percent} onChange={e => set('consignor_split_percent',e.target.value)} /></div>
          </> : <div style={s.ff}><label style={s.lbl}>Fleet Item (optional)</label><select style={s.inp} value={form.rental_item_id} onChange={e => set('rental_item_id',e.target.value)}><option value="">None</option>{fleetItems.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}</select></div>}
          <div style={{ ...s.ff, gridColumn:'1/-1' }}><label style={s.lbl}>Title *</label><input style={s.inp} value={form.title} onChange={e => set('title',e.target.value)} placeholder="e.g. 2019 Husqvarna PG 820" /></div>
          {[['Make','make'],['Model','model'],['Year','year'],['Serial #','serial_number'],['Hour Meter','hour_meter']].map(([l,k]) => (
            <div key={k} style={s.ff}><label style={s.lbl}>{l}</label><input style={s.inp} value={form[k]} onChange={e => set(k,e.target.value)} /></div>
          ))}
          <div style={s.ff}><label style={s.lbl}>Condition</label><select style={s.inp} value={form.condition} onChange={e => set('condition',e.target.value)}>{Object.entries(CONDITION_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          <div style={s.ff}><label style={s.lbl}>Asking Price ($) *</label><input style={s.inp} type="number" value={form.asking_price} onChange={e => set('asking_price',e.target.value)} /></div>
          <div style={s.ff}><label style={s.lbl}>Min Price ($)</label><input style={s.inp} type="number" value={form.minimum_price} onChange={e => set('minimum_price',e.target.value)} /></div>
          {form.sale_type==='owned' && <div style={s.ff}><label style={s.lbl}>Cost Basis ($)</label><input style={s.inp} type="number" value={form.cost_basis} onChange={e => set('cost_basis',e.target.value)} /></div>}
        </div>
        {error && <div style={s.err}>{error}</div>}
      </div>
    </div>
  );
}

function RecordSaleForm({ item, clients, onSaved, onCancel }) {
  const [form, setForm] = useState({ client_id:'', buyer_name:'', sale_price:item.asking_price||'', tax_percent:0, payment_method:'cash', amount_paid:item.asking_price||'', sale_date:new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k,v) => setForm(p => ({ ...p, [k]:v }));
  const price = parseFloat(form.sale_price||0);
  const tax = price * parseFloat(form.tax_percent||0) / 100;
  const total = price + tax;
  const split = parseFloat(item.consignor_split_percent||70);
  const consAmt = item.sale_type==='consignment' ? price*split/100 : 0;

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const res = await axiosClient.post('/api/rentals/sales/items/' + item.id + '/sell', { ...form, client_id: form.client_id||null });
      const json = res.data;
      if (res.status >= 400) throw new Error(json.error||'Failed');
      onSaved();
    } catch(e) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <div style={s.page}>
      <div style={s.hdr}>
        <div><h2 style={s.title}>Record Sale</h2><p style={s.sub}>{item.title}</p></div>
        <div style={{ display:'flex', gap:10 }}><button style={s.btnSec} onClick={onCancel}>Cancel</button><button style={s.btn} onClick={submit} disabled={saving}>{saving?'Saving...':'Confirm Sale'}</button></div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 260px', gap:16 }}>
        <div style={s.fBox}>
          <div style={s.fGrid}>
            <div style={{ ...s.ff, gridColumn:'1/-1' }}><label style={s.lbl}>Client</label><select style={s.inp} value={form.client_id} onChange={e => set('client_id',e.target.value)}><option value="">Manual entry</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            {!form.client_id && <div style={s.ff}><label style={s.lbl}>Buyer Name</label><input style={s.inp} value={form.buyer_name} onChange={e => set('buyer_name',e.target.value)} /></div>}
            <div style={s.ff}><label style={s.lbl}>Sale Price ($)</label><input style={s.inp} type="number" value={form.sale_price} onChange={e => { set('sale_price',e.target.value); set('amount_paid',e.target.value); }} /></div>
            <div style={s.ff}><label style={s.lbl}>Tax %</label><input style={s.inp} type="number" value={form.tax_percent} onChange={e => set('tax_percent',e.target.value)} /></div>
            <div style={s.ff}><label style={s.lbl}>Payment</label><select style={s.inp} value={form.payment_method} onChange={e => set('payment_method',e.target.value)}>{['cash','check','card','wire','financing','other'].map(m => <option key={m} value={m}>{m}</option>)}</select></div>
            <div style={s.ff}><label style={s.lbl}>Sale Date</label><input style={s.inp} type="date" value={form.sale_date} onChange={e => set('sale_date',e.target.value)} /></div>
          </div>
        </div>
        <div style={{ ...s.fBox, alignSelf:'start' }}>
          <div style={{ fontSize:11,fontWeight:700,color:'#e2e8f0',textTransform:'uppercase',marginBottom:10 }}>Summary</div>
          <div style={s.sumLine}><span>Sale Price</span><span>${price.toFixed(2)}</span></div>
          {tax>0 && <div style={s.sumLine}><span>Tax</span><span>${tax.toFixed(2)}</span></div>}
          <div style={{ ...s.sumLine, fontWeight:700, color:'#fff', fontSize:15 }}><span>Total</span><span>${total.toFixed(2)}</span></div>
          {item.sale_type==='consignment' && <>
            <div style={{ height:1,background:'#222',margin:'10px 0' }} />
            <div style={{ ...s.sumLine, color:'#f0c040' }}><span>Consignor ({split}%)</span><span>${consAmt.toFixed(2)}</span></div>
            <div style={{ ...s.sumLine, color:'#4caf50', fontWeight:700 }}><span>Your Net</span><span>${(total-consAmt).toFixed(2)}</span></div>
          </>}
        </div>
      </div>
      {error && <div style={s.err}>{error}</div>}
    </div>
  );
}

function SaleOrdersPage({ onBack }) {
  const [orders, setOrders] = useState([]);
  useEffect(() => { axiosClient.get('/api/rentals/sales/orders').then(r=>setOrders(r.data.orders||[])); }, []);
  const payout = async (id) => {
    await axiosClient.post('/api/rentals/sales/orders/'+id+'/payout');
    axiosClient.get('/api/rentals/sales/orders').then(r=>setOrders(r.data.orders||[]));
  };
  return (
    <div style={s.page}>
      <div style={s.hdr}><h2 style={s.title}>Sale History</h2><button style={s.btnSec} onClick={onBack}>Back</button></div>
      {orders.length===0 ? <div style={s.empty}>No sales yet.</div> : orders.map(o => (
        <div key={o.id} style={{ background:'#111',border:'1px solid #222',borderRadius:8,padding:'12px 16px',marginBottom:8,display:'flex',gap:16,alignItems:'center',flexWrap:'wrap',fontSize:13 }}>
          <span style={{ fontWeight:700,color:'#4ab0f0',fontFamily:'monospace' }}>{o.sale_number}</span>
          <span>{o.rental_sale_items?.title||'—'}</span>
          <span style={{ color:'#e2e8f0' }}>{o.sale_date?new Date(o.sale_date).toLocaleDateString():'—'}</span>
          <span style={{ color:'#fff',fontWeight:700 }}>${parseFloat(o.total_amount||0).toFixed(2)}</span>
          {o.consignor_amount>0 && <span style={{ color:'#f0c040' }}>Consignor: ${parseFloat(o.consignor_amount).toFixed(2)}</span>}
          <span style={{ color:'#4caf50',fontWeight:700,marginLeft:'auto' }}>Net: ${parseFloat(o.your_cut||0).toFixed(2)}</span>
          {o.consignor_amount>0 && !o.consignor_paid && <button style={{ background:'#2a2a1a',color:'#f0c040',border:'1px solid #3a3a1a',borderRadius:4,padding:'4px 10px',fontSize:11,cursor:'pointer' }} onClick={() => payout(o.id)}>Mark Paid</button>}
          {o.consignor_paid && <span style={{ color:'#4caf50',fontSize:11 }}>Paid</span>}
        </div>
      ))}
    </div>
  );
}

function ConsignorsPage({ consignors:initial, onBack }) {
  const [list, setList] = useState(initial);
  const [form, setForm] = useState({ name:'', company:'', phone:'', default_split_percent:70 });
  const set = (k,v) => setForm(p => ({ ...p,[k]:v }));
  const add = async () => {
    if (!form.name) return;
    const res = await axiosClient.post('/api/rentals/sales/consignors', form);
    const j = res.data;
    setList(p => [...p, j.consignor]);
    setForm({ name:'', company:'', phone:'', default_split_percent:70 });
  };
  return (
    <div style={s.page}>
      <div style={s.hdr}><h2 style={s.title}>Consignors</h2><button style={s.btnSec} onClick={onBack}>Back</button></div>
      <div style={s.fBox}>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <input style={{ ...s.inp,flex:1,minWidth:130 }} placeholder="Name *" value={form.name} onChange={e => set('name',e.target.value)} />
          <input style={{ ...s.inp,flex:1,minWidth:120 }} placeholder="Company" value={form.company} onChange={e => set('company',e.target.value)} />
          <input style={{ ...s.inp,flex:1,minWidth:110 }} placeholder="Phone" value={form.phone} onChange={e => set('phone',e.target.value)} />
          <input style={{ ...s.inp,width:70 }} type="number" placeholder="Split%" value={form.default_split_percent} onChange={e => set('default_split_percent',e.target.value)} />
          <button style={s.btn} onClick={add}>Add</button>
        </div>
      </div>
      {list.map(c => (
        <div key={c.id} style={{ background:'#111',border:'1px solid #222',borderRadius:8,padding:'12px 16px',marginBottom:8,display:'flex',gap:16,alignItems:'center',fontSize:13 }}>
          <span style={{ fontWeight:700 }}>{c.name}</span>
          {c.company && <span style={{ color:'#e2e8f0' }}>{c.company}</span>}
          {c.phone && <span style={{ color:'#e2e8f0' }}>{c.phone}</span>}
          <span style={{ color:'#c080f0',marginLeft:'auto',fontWeight:700 }}>{c.default_split_percent}% to them</span>
        </div>
      ))}
    </div>
  );
}

const s = {
  page:{ padding:24, maxWidth:1200, margin:'0 auto', color:'#eee' },
  hdr:{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 },
  title:{ fontSize:22, fontWeight:700, color:'#fff', margin:0 },
  sub:{ fontSize:13, color:'#e2e8f0', margin:'4px 0 0' },
  mRow:{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:20 },
  mCard:{ background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:8, padding:'12px 18px', minWidth:100, textAlign:'center' },
  mLbl:{ fontSize:11, color:'#e2e8f0', marginTop:2, textTransform:'uppercase' },
  fRow:{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' },
  search:{ flex:1, minWidth:200, background:'#111', border:'1px solid #333', borderRadius:6, color:'#eee', padding:'8px 12px', fontSize:13 },
  sel:{ background:'#111', border:'1px solid #333', borderRadius:6, color:'#eee', padding:'8px 12px', fontSize:13 },
  grid:{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 },
  card:{ background:'#111', border:'1px solid #222', borderRadius:10, overflow:'hidden' },
  cardImg:{ height:150, background:'#1a1a1a', position:'relative', display:'flex', alignItems:'center', justifyContent:'center' },
  pill:{ position:'absolute', borderRadius:4, padding:'3px 8px', fontSize:11, fontWeight:700 },
  cardBody:{ padding:14 },
  empty:{ padding:40, textAlign:'center', color:'#e2e8f0' },
  btn:{ background:'#4caf50', color:'#fff', border:'none', borderRadius:6, padding:'8px 18px', fontSize:13, fontWeight:600, cursor:'pointer' },
  btnSec:{ background:'#222', color:'#e2e8f0', border:'1px solid #333', borderRadius:6, padding:'8px 18px', fontSize:13, cursor:'pointer' },
  btnSell:{ background:'#4caf50', color:'#fff', border:'none', borderRadius:5, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer' },
  btnPend:{ background:'#2a2a1a', color:'#f0c040', border:'1px solid #3a3a1a', borderRadius:5, padding:'6px 10px', fontSize:11, cursor:'pointer' },
  fBox:{ background:'#111', border:'1px solid #222', borderRadius:8, padding:20, marginBottom:16 },
  fGrid:{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'14px 20px' },
  ff:{ display:'flex', flexDirection:'column', gap:5 },
  lbl:{ fontSize:12, color:'#e2e8f0', fontWeight:500 },
  inp:{ background:'#0a0a0a', border:'1px solid #2a2a2a', borderRadius:5, color:'#eee', padding:'7px 10px', fontSize:13, outline:'none' },
  tBtn:{ background:'#1a1a1a', border:'1px solid #333', color:'#777', borderRadius:6, padding:'8px 18px', fontSize:13, cursor:'pointer' },
  tBtnA:{ background:'#1a2a1a', border:'1px solid #4caf50', color:'#4caf50' },
  sumLine:{ display:'flex', justifyContent:'space-between', padding:'5px 0', fontSize:13, color:'#e2e8f0', borderBottom:'1px solid #1a1a1a' },
  err:{ marginTop:12, padding:'10px 14px', background:'#2a1a1a', color:'#e57373', borderRadius:6, fontSize:13 },
};